import { getScorePass, selectSkillLevelInputValue } from '@/domains/scoring';
import { getSkillLevel } from '@/domains';
import { PassFailTag, RoleplayCardProps, SkillLevelTag, TagProps } from '@/components';
import { TSimulationType } from '@/types';
import React from 'react';

export interface RoleplayTagProps extends TagProps {
  history: RoleplayCardProps['history'];
  showSkillLevelAs?: RoleplayCardProps['showSkillLevelAs'];
  simulationType?: TSimulationType;
}

/**
 * RoleplayTag
 *
 * A component that conditionally renders either a
 * SkillLevelTag or PassFailTag based on the simulation type.
 *
 * - For 'role-play' simulation type, it renders a SkillLevelTag based on the provided score.
 * - For 'practice' and 'certification' simulation types, it renders a PassFailTag
 *    with the appropriate variant based on the score (pass/fail).
 * - If the simulation type is not recognized or if necessary props are missing, it renders nothing.
 *
 * This component serves as a unified interface for displaying tags related to role-play simulations,
 * abstracting away the specific tag types used for different simulation contexts.
 */
export const RoleplayTag: React.FC<RoleplayTagProps> = ({
  className,
  history = [],
  showSkillLevelAs = 'latest',
  simulationType,
  variant,
  ...props
}) => {
  // If no simulation type is provided, we don't render a tag.
  if (!simulationType) {
    return null;
  }

  const latestScore = history.length > 0 ? history[0]?.score : null;
  const highestScore = history.reduce((max, score) => {
    const scoreValue = selectSkillLevelInputValue({
      fallback: 0,
      score: score.score,
      scorePercentage: score.scorePercentage,
    });

    return scoreValue > max ? scoreValue : max;
  }, 0);
  
  const scoreToUse = showSkillLevelAs === 'latest' ? latestScore : highestScore;
  const skillLevel =
    history.length > 0 ? getSkillLevel(scoreToUse) : undefined;

  // If we can't determine the skill level, we don't render a tag.
  if (!skillLevel) {
    return null;
  }

  /**
   * Determine if the user has passed based on the latest score and skill level.
   */
  const pass = getScorePass(undefined, simulationType, history[0]);

  if (simulationType === 'role-play') {
    return <SkillLevelTag skillLevel={skillLevel} {...props} />;
  }

  if (simulationType === 'practice' || simulationType === 'certification') {
    const variant = pass ? 'pass' : 'fail';
    return <PassFailTag simulationType={simulationType} variant={variant} {...props} />;
  }

  return null;
};

RoleplayTag.displayName = 'RoleplayTag';

export default RoleplayTag;
